import type {
    CreateBookingDetails,
    UpdateBookingRequest,
} from "@albertine/types/openapi/lmt";
import type {
    Attachment,
    AttachmentWithFile,
    BookingAttachment,
} from "../common/types/Attachment";
import type { BookingUpdatables } from "../common/types/Booking";
import { fromTimestampToIsoDateString } from "./timestamp.util";

export function makePrivate(
    attachment: Attachment | AttachmentWithFile | BookingAttachment,
): BookingAttachment {
    return {
        ...attachment,
        isPublic: false,
    };
}

export function makePublic(
    attachment: Attachment | AttachmentWithFile | BookingAttachment,
): BookingAttachment {
    return {
        ...attachment,
        isPublic: true,
    };
}

/** Compile error naming `T` unless it is `never`. */
type AssertNever<T extends never> = T;

/**
 * Maps LMT booking form updates to the PATCH /lmt/bookings/:bookingId JSON body.
 * Converts Firebase timestamps to ISO strings and `suppliers` to `supplierIds`.
 *
 * Every `BookingUpdatables` field is either mapped into the request body or
 * destructured out by name below. A new field that is neither fails to compile here,
 * rather than being silently dropped by the server's request validation.
 */
export function toUpdateBookingApiJson(
    updates: Partial<BookingUpdatables>,
): UpdateBookingRequest {
    const {
        suppliers,
        startTime,
        endTime,
        // Derived or owned by the server, or moved to a dedicated endpoint.
        offeringCategory: _offeringCategory,
        country: _country,
        googleUrl: _googleUrl,
        geoPoint: _geoPoint,
        numberOfNights: _numberOfNights,
        archived: _archived,
        reservationStatus: _reservationStatus,
        notesForMemberSenderId: _notesForMemberSenderId,
        notesForMemberSenderName: _notesForMemberSenderName,
        attachments: _attachments,
        images: _images,
        imageURL: _imageURL,
        croppedImageURL: _croppedImageURL,
        absoluteCrop: _absoluteCrop,
        relativeCrop: _relativeCrop,
        ...rest
    } = updates;

    type _AllFieldsAreMappedOrDropped = AssertNever<
        Exclude<keyof typeof rest, keyof UpdateBookingRequest>
    >;

    return {
        ...rest,
        ...(suppliers !== undefined ? { supplierIds: suppliers } : {}),
        ...(startTime !== undefined
            ? {
                  startTime: startTime ? fromTimestampToIsoDateString(startTime) : null,
              }
            : {}),
        ...(endTime !== undefined
            ? {
                  endTime: endTime ? fromTimestampToIsoDateString(endTime) : null,
              }
            : {}),
    };
}

export type CreateBookingApiPayload = {
    requestId: string;
    catalogItemId?: string | null;
    details?: CreateBookingDetails;
};

function getPriceAndPaymentQuoteLabel(
    priceQuote: "final" | "estimate" | undefined | null,
    paymentTo: "albertine" | "supplier" | undefined | null,
): string {
    if (paymentTo === "albertine" && priceQuote === "estimate") {
        return "Price is an estimate, confirmed at the time of booking.";
    }
    if (paymentTo === "supplier" && priceQuote === "estimate") {
        return "Price is an estimate, confirmed at the time of booking. You will pay directly at the property.";
    }
    if (paymentTo === "supplier" && priceQuote === "final") {
        return "You will pay directly at the property.";
    }
    if (paymentTo === "supplier") {
        return "You will pay directly at the property.";
    }
    if (priceQuote === "estimate") {
        return "Price is an estimate, confirmed at the time of booking.";
    }
    return "";
}

function getAllPriceAndPaymentQuoteVariations(): string[] {
    return [
        getPriceAndPaymentQuoteLabel("final", "albertine"),
        getPriceAndPaymentQuoteLabel("estimate", "albertine"),
        getPriceAndPaymentQuoteLabel("final", "supplier"),
        getPriceAndPaymentQuoteLabel("estimate", "supplier"),
        getPriceAndPaymentQuoteLabel("final", null),
        getPriceAndPaymentQuoteLabel("estimate", null),
        getPriceAndPaymentQuoteLabel(null, "albertine"),
        getPriceAndPaymentQuoteLabel(null, "supplier"),
    ];
}

export function updateBookingPriceDetails(
    current: string | undefined | null,
    priceQuote: "final" | "estimate" | undefined | null,
    paymentTo: "albertine" | "supplier" | undefined | null,
): { priceDetails: string } | undefined {
    if (
        !current ||
        current === "" ||
        getAllPriceAndPaymentQuoteVariations().includes(current)
    ) {
        const quote = getPriceAndPaymentQuoteLabel(priceQuote, paymentTo);
        if (quote) {
            return {
                priceDetails: getPriceAndPaymentQuoteLabel(priceQuote, paymentTo),
            };
        }
    }
    return undefined;
}
